/**
 * Edit Service Dialog Component
 * @description 성능 최적화된 서비스 수정 다이얼로그 (로컬 state + 조건부 마운트)
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
} from '@shared/ui/primitives';
import { ServiceIconPicker } from './ServiceIconPicker';

export interface ServiceConfig {
  folder: string;
  name: string;
  description: string;
  labels: string[];
  icon: string;
  ports: {
    frontend: number;
    backend: number;
    'frontend-admin'?: number;
  };
  adminRoute?: string;
  displayOrder?: number;
}

interface EditServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service: ServiceConfig;
  existingServices: ServiceConfig[];
  onSave: (updatedService: ServiceConfig) => Promise<void>;
}

const EditServiceDialogComponent: React.FC<EditServiceDialogProps> = ({
  open,
  onOpenChange,
  service,
  existingServices,
  onSave,
}) => {
  const [folder, setFolder] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('Shield');
  const [labelsInput, setLabelsInput] = useState('');
  const [frontendPort, setFrontendPort] = useState(0);
  const [backendPort, setBackendPort] = useState(0);
  const [adminPort, setAdminPort] = useState<number | undefined>(undefined);
  const [adminRoute, setAdminRoute] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setFolder(service.folder);
      setName(service.name);
      setDescription(service.description);
      setIcon(service.icon || 'Shield');
      setLabelsInput(service.labels.join(', '));
      setFrontendPort(service.ports.frontend || 0);
      setBackendPort(service.ports.backend || 0);
      setAdminPort(service.ports['frontend-admin']);
      setAdminRoute(service.adminRoute);
      setIsLoading(false);
    }
  }, [open, service]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  }, []);

  const handleLabelsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLabelsInput(e.target.value);
  }, []);

  const handleIconChange = useCallback((iconName: string) => {
    setIcon(iconName);
  }, []);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSave = useCallback(async () => {
    if (!folder.trim()) {
      alert('폴더명을 입력해주세요.');
      return;
    }
    if (!name.trim()) {
      alert('서비스명을 입력해주세요.');
      return;
    }
    if (!description.trim()) {
      alert('설명을 입력해주세요.');
      return;
    }

    const hasFrontend = Number(frontendPort) > 0;
    const hasBackend = Number(backendPort) > 0;
    const hasAdmin = Number(adminPort) > 0;
    if (!hasFrontend && !hasBackend && !hasAdmin) {
      alert('Frontend, Backend, Admin 중 최소 하나의 포트를 설정해주세요.');
      return;
    }

    const trimmedFolder = folder.trim();
    const trimmedName = name.trim();

    const duplicateFolder = existingServices.find(
      (s) => s.folder === trimmedFolder && s.folder !== service.folder
    );
    if (duplicateFolder) {
      alert('이미 존재하는 폴더명입니다.');
      return;
    }

    const duplicateName = existingServices.find(
      (s) => s.name.trim() === trimmedName && s.folder !== service.folder
    );
    if (duplicateName) {
      alert('이미 존재하는 서비스 이름입니다.');
      return;
    }

    const labels = labelsInput
      .split(',')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    const updatedService: ServiceConfig = {
      ...service,
      folder: trimmedFolder,
      name: trimmedName,
      description: description.trim(),
      labels,
      icon,
      ports: {
        frontend: hasFrontend ? Number(frontendPort) : 0,
        backend: hasBackend ? Number(backendPort) : 0,
        ...(hasAdmin ? { 'frontend-admin': Number(adminPort) } : {}),
      },
      adminRoute: adminRoute?.trim() || undefined,
    };

    setIsLoading(true);
    try {
      await onSave(updatedService);
      onOpenChange(false);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      alert(`저장 실패: ${msg}`);
      console.error('Error saving service:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    folder,
    name,
    description,
    labelsInput,
    icon,
    frontendPort,
    backendPort,
    adminPort,
    adminRoute,
    service,
    existingServices,
    onSave,
    onOpenChange,
  ]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>서비스 수정</DialogTitle>
          <DialogDescription>서비스 정보를 수정합니다.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="edit-folder" className="text-sm font-medium">
              폴더명 <span className="text-red-500">*</span>
            </label>
            <Input id="edit-folder" value={folder} disabled placeholder="예: asset-topology" />
          </div>
          <div className="grid gap-2">
            <label htmlFor="edit-name" className="text-sm font-medium">
              서비스명 <span className="text-red-500">*</span>
            </label>
            <Input
              id="edit-name"
              value={name}
              onChange={handleNameChange}
              placeholder="예: 자산 Topology"
            />
          </div>
          <div className="grid gap-2">
            <label htmlFor="edit-description" className="text-sm font-medium">
              설명 <span className="text-red-500">*</span>
            </label>
            <Input
              id="edit-description"
              value={description}
              onChange={handleDescriptionChange}
              placeholder="서비스 설명을 입력하세요"
            />
          </div>
          <div className="grid gap-2">
            <label htmlFor="edit-labels" className="text-sm font-medium">
              라벨 (쉼표로 구분)
            </label>
            <Input
              id="edit-labels"
              value={labelsInput}
              onChange={handleLabelsChange}
              placeholder="예: Asset Management, Service"
            />
            <p className="text-xs text-muted-foreground">
              첫 번째 비-Service 라벨이 Tenant Entry 좌측 메뉴 그룹·메뉴관리 정책 이름이 됩니다.
            </p>
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium">아이콘</label>
            <ServiceIconPicker value={icon} onChange={handleIconChange} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="grid gap-2">
              <label htmlFor="edit-frontend-port" className="text-sm font-medium">
                Frontend Port <span className="text-red-500">*</span>
              </label>
              <Input
                id="edit-frontend-port"
                type="number"
                value={frontendPort || ''}
                onChange={(e) => setFrontendPort(parseInt(e.target.value, 10) || 0)}
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="edit-backend-port" className="text-sm font-medium">
                Backend Port <span className="text-red-500">*</span>
              </label>
              <Input
                id="edit-backend-port"
                type="number"
                value={backendPort || ''}
                onChange={(e) => setBackendPort(parseInt(e.target.value, 10) || 0)}
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="edit-admin-port" className="text-sm font-medium">
                Frontend Admin Port (선택)
              </label>
              <Input
                id="edit-admin-port"
                type="number"
                value={adminPort || ''}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  setAdminPort(Number.isNaN(parsed) ? undefined : parsed || undefined);
                }}
              />
            </div>
          </div>
          <div className="grid gap-2">
            <label htmlFor="edit-admin-route" className="text-sm font-medium">
              Admin Route (선택)
            </label>
            <Input
              id="edit-admin-route"
              value={adminRoute || ''}
              onChange={(e) => setAdminRoute(e.target.value || undefined)}
              placeholder="예: /admin?tab=service"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            취소
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? '저장 중...' : '저장'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EditServiceDialog = React.memo(EditServiceDialogComponent);

EditServiceDialog.displayName = 'EditServiceDialog';
